import {reactive, watch, toRefs, onMounted} from 'vue'
import {useRoute} from 'vue-router'
import auth from 'shared/plugins/auth'
import {api} from 'shared/plugins/api'
import {useStore} from 'shared/stores/store'
import createQueryString from 'shared/utils/createQueryString'
import isValidUuid from 'shared/utils/isValidUuid'

export const useCanUserJoinWorkspace = () => {
	const route = useRoute()
	const store = useStore()

	const state = reactive({
		isLoading: true,
		canUserJoinWorkspace: false,
		workspaceUserCanJoin: null,
		isAlreadyWorkspaceMember: false,
		workspaceId: route.params.workspaceId,
		sessionId: route.params.sessionId,
		errorCode: null,
		errorTitle: null,
		errorMessage: null,
	})

	watch(
		() => auth.isAuthenticated.value,
		isAuthenticated => {
			if (isAuthenticated || auth.loading.value) return
			auth.login({redirectAfterLogin: route.fullPath})
		},
		{immediate: true}
	)

	onMounted(async () => {
		// the endpoint's schema requires workspaceId to be a valid uuid
		// so to avoid 400 errors, we check if the workspaceId is valid before making the request
		if (state.workspaceId && !isValidUuid(state.workspaceId)) {
			state.errorCode = 'workspace_not_found'
			state.errorTitle = 'This workspace does not exist'
			state.isLoading = false
			return
		}

		if (!auth.isAuthenticated.value) return
		// check if user can join
		try {
			const queryString = createQueryString(
				state.workspaceId ? {workspaceId: state.workspaceId} : {sessionId: state.sessionId}
			)
			const data = await api.get('/workspace/can-user-join' + queryString)
			state.canUserJoinWorkspace = data.canJoin
			if (state.canUserJoinWorkspace) {
				state.workspaceUserCanJoin = data.workspace
				state.isAlreadyWorkspaceMember = await store.getIsUserWorkspaceMember(state.workspaceUserCanJoin?.id)
				state.inviteType = data.workspace.inviteType
				state.invitedAs = data.workspace.invitedAs
				return
			}

			state.errorCode = data.errorCode
			state.errorTitle = data.title
			state.errorMessage = data.message
		} finally {
			state.isLoading = false
		}
	})

	return {
		...toRefs(state),
	}
}
